import {
  type AttachmentBlockModel,
  type ImageBlockProps,
  MAX_IMAGE_WIDTH,
} from '@blocksuite/affine-model';
import {
  EMBED_CARD_HEIGHT,
  EMBED_CARD_WIDTH,
} from '@blocksuite/affine-shared/consts';
import {
  edgelessToolbarI18nT,
  FileSizeLimitProvider,
} from '@blocksuite/affine-shared/services';
import {
  readImageSize,
  transformModel,
  withTempBlobData,
} from '@blocksuite/affine-shared/utils';
import type { Container } from '@blocksuite/global/di';
import { createIdentifier } from '@blocksuite/global/di';
import { Bound } from '@blocksuite/global/gfx';
import {
  DownloadIcon,
  ExpandFullIcon,
  MoreVerticalIcon,
  PausePanelIcon,
  PlayFillIcon,
  VolumePanelIcon,
} from '@blocksuite/icons/lit';
import { type BlockStdScope, StdIdentifier } from '@blocksuite/std';
import type {
  AttachmentMediaPresenceState,
  ExtensionType,
} from '@blocksuite/store';
import { Extension } from '@blocksuite/store';
import {
  css,
  html,
  LitElement,
  nothing,
  type PropertyValues,
  type TemplateResult,
} from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  getAttachmentBlob,
  getVideoEmbedHeightForWidth,
  readVideoSize,
  resolveAttachmentBlockDisplayName,
} from './utils';

export const HTML_ATTACHMENT_FOCUS_OUT_EVENT =
  'affine-html-attachment-focusout';

declare global {
  interface HTMLElementEventMap {
    [HTML_ATTACHMENT_FOCUS_OUT_EVENT]: CustomEvent<null>;
  }
}

export type AttachmentMediaPresenceUpdate = Pick<
  AttachmentMediaPresenceState,
  | 'action'
  | 'currentTime'
  | 'duration'
  | 'mediaKind'
  | 'muted'
  | 'playbackRate'
  | 'playing'
>;

export type AttachmentMediaFollowOptions = {
  muted: boolean;
};

export type AttachmentMediaPresenceTarget = {
  applyRemotePresence: (
    presence: AttachmentMediaPresenceState,
    options: AttachmentMediaFollowOptions
  ) => void;
};

export const ATTACHMENT_MEDIA_PRESENCE_TARGET_ATTRIBUTE =
  'data-attachment-media-presence-target';

export const ATTACHMENT_MEDIA_PRESENCE_TARGET_SELECTOR = `[${ATTACHMENT_MEDIA_PRESENCE_TARGET_ATTRIBUTE}="true"]`;

export type AttachmentMediaPresenceRemote = {
  clientId: number;
  color?: string;
  presence: AttachmentMediaPresenceState;
  userName: string;
};

export type AttachmentEmbedRenderContext = {
  interactive: boolean;
  loadBlob?: () => Promise<Blob | null>;
  publishMediaPresence?: (update: AttachmentMediaPresenceUpdate) => void;
  selected: boolean;
  std: BlockStdScope;
};

export type AttachmentEmbedConfig = {
  name: string;
  /**
   * Check if the attachment can be turned into embed view.
   */
  check: (model: AttachmentBlockModel, maxFileSize: number) => boolean;
  /**
   * The action will be executed when the 「Turn into embed view」 button is clicked.
   */
  action?: (
    model: AttachmentBlockModel,
    std: BlockStdScope
  ) => Promise<void> | void;
  /**
   * Renders the embed view.
   */
  render?: (
    model: AttachmentBlockModel,
    blobUrl: string,
    context: AttachmentEmbedRenderContext
  ) => TemplateResult | null;

  /**
   * Allows an embed to render its own loading shell before ResourceController
   * has a blob URL. Media players that own their local loading state can use
   * this to avoid falling back to the generic attachment card during uploads.
   */
  renderWithoutBlob?: boolean;

  /**
   * Should show status when turned on.
   */
  shouldShowStatus?: boolean;

  /**
   * Re-render the embedded content when selection/interactive state changes.
   * Only embeds that consume AttachmentEmbedRenderContext should opt in;
   * remounting heavyweight previews like PDFs on every canvas click causes
   * visible reload flicker.
   */
  rerenderOnContextChange?: boolean;

  /**
   * Should block type conversion be required.
   */
  shouldBeConverted?: boolean;
};

// Single embed config.
export const AttachmentEmbedConfigIdentifier =
  createIdentifier<AttachmentEmbedConfig>(
    'AffineAttachmentEmbedConfigIdentifier'
  );

export function AttachmentEmbedConfigExtension(
  configs: AttachmentEmbedConfig[] = embedConfig
): ExtensionType {
  return {
    setup: di => {
      configs.forEach(option => {
        di.addImpl(AttachmentEmbedConfigIdentifier(option.name), () => option);
      });
    },
  };
}

// A embed config map.
export const AttachmentEmbedConfigMapIdentifier = createIdentifier<
  Map<string, AttachmentEmbedConfig>
>('AffineAttachmentEmbedConfigMapIdentifier');

export const AttachmentEmbedProvider = createIdentifier<AttachmentEmbedService>(
  'AffineAttachmentEmbedProvider'
);

export class AttachmentEmbedService extends Extension {
  private get _maxFileSize() {
    return this.std.get(FileSizeLimitProvider).maxFileSize;
  }

  get keys() {
    return this.configs.keys();
  }

  get values() {
    return this.configs.values();
  }

  get configs(): Map<string, AttachmentEmbedConfig> {
    return this.std.get(AttachmentEmbedConfigMapIdentifier);
  }

  constructor(private readonly std: BlockStdScope) {
    super();
  }

  static override setup(di: Container) {
    di.addImpl(AttachmentEmbedConfigMapIdentifier, provider =>
      provider.getAll(AttachmentEmbedConfigIdentifier)
    );
    di.addImpl(AttachmentEmbedProvider, this, [StdIdentifier]);
  }

  // Converts to embed view.
  convertTo(model: AttachmentBlockModel, maxFileSize = this._maxFileSize) {
    const config = this.values.find(config => config.check(model, maxFileSize));

    if (config?.action) {
      config.action(model, this.std)?.catch(console.error);
      return;
    }

    model.store.updateBlock(model, { embed: true });
  }

  embedded(model: AttachmentBlockModel, maxFileSize = this._maxFileSize) {
    return this.values.some(config => config.check(model, maxFileSize));
  }

  getConfig(model: AttachmentBlockModel, maxFileSize = this._maxFileSize) {
    return this.values.find(config => config.check(model, maxFileSize)) ?? null;
  }

  getRender(model: AttachmentBlockModel, maxFileSize = this._maxFileSize) {
    return this.getConfig(model, maxFileSize)?.render ?? null;
  }

  canRenderWithoutBlob(
    model: AttachmentBlockModel,
    maxFileSize = this._maxFileSize
  ) {
    return this.getConfig(model, maxFileSize)?.renderWithoutBlob ?? false;
  }

  shouldShowStatus(
    model: AttachmentBlockModel,
    maxFileSize = this._maxFileSize
  ) {
    return this.getConfig(model, maxFileSize)?.shouldShowStatus ?? false;
  }

  shouldRerenderOnContextChange(
    model: AttachmentBlockModel,
    maxFileSize = this._maxFileSize
  ) {
    return this.getConfig(model, maxFileSize)?.rerenderOnContextChange ?? false;
  }

  shouldBeConverted(
    model: AttachmentBlockModel,
    maxFileSize = this._maxFileSize
  ) {
    return this.getConfig(model, maxFileSize)?.shouldBeConverted ?? false;
  }
}

function isHtmlAttachment(model: AttachmentBlockModel): boolean {
  const type = model.props.type.toLowerCase();
  const name = resolveAttachmentBlockDisplayName(model).toLowerCase();
  return (
    type === 'text/html' ||
    type === 'application/xhtml+xml' ||
    name.endsWith('.html') ||
    name.endsWith('.htm')
  );
}

const TEXT_ATTACHMENT_PREVIEW_MAX_BYTES = 1024 * 1024;

function isTextAttachment(model: AttachmentBlockModel): boolean {
  const type = model.props.type.toLowerCase();
  const name = resolveAttachmentBlockDisplayName(model).toLowerCase();
  return (
    (type.startsWith('text/') && !isHtmlAttachment(model)) ||
    type === 'application/json' ||
    type === 'application/xml' ||
    name.endsWith('.txt') ||
    name.endsWith('.md') ||
    name.endsWith('.markdown') ||
    name.endsWith('.json') ||
    name.endsWith('.xml')
  );
}

@customElement('affine-text-attachment-embed')
export class TextAttachmentEmbed extends LitElement {
  static override styles = css`
    :host {
      display: block;
      box-sizing: border-box;
      width: 100%;
      height: 100%;
      overflow: auto;
      background: var(--affine-background-primary-color, #fff);
      color: var(--affine-text-primary-color, #1f1f1f);
    }

    pre {
      box-sizing: border-box;
      min-width: 100%;
      min-height: 100%;
      margin: 0;
      padding: 20px;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      font:
        13px/1.55 ui-monospace,
        SFMono-Regular,
        Menlo,
        Monaco,
        Consolas,
        monospace;
    }

    .state {
      display: grid;
      min-height: 100%;
      place-items: center;
      padding: 20px;
      color: var(--affine-text-secondary-color, #777);
      font:
        13px/1.5 -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        sans-serif;
    }
  `;

  @property({ attribute: false })
  accessor loadBlob: (() => Promise<Blob | null>) | undefined;

  @state()
  private accessor _content: string | null = null;

  @state()
  private accessor _error = false;

  protected override updated(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('loadBlob')) {
      void this._load();
    }
  }

  private async _load() {
    this._content = null;
    this._error = false;
    try {
      const blob = await this.loadBlob?.();
      if (!blob) throw new Error('Text attachment blob is unavailable');
      this._content = await blob.text();
    } catch (error) {
      this._error = true;
      console.error(error);
    }
  }

  override render() {
    if (this._error) {
      return html`<div class="state">
        ${edgelessToolbarI18nT('toolbar.textPreviewUnavailable')}
      </div>`;
    }
    if (this._content === null) {
      return html`<div class="state">
        ${edgelessToolbarI18nT('toolbar.loadingPreview')}
      </div>`;
    }
    return html`<pre>${this._content}</pre>`;
  }
}

function getHtmlEmbedHeight() {
  return EMBED_CARD_HEIGHT.html;
}

const VIDEO_PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

function stopVideoEmbedEvent(event: Event) {
  event.stopPropagation();
}

function formatVideoTime(time: number) {
  if (!Number.isFinite(time) || time < 0) return '0:00';

  const totalSeconds = Math.floor(time);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  const paddedSeconds = seconds.toString().padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${paddedSeconds}`;
  }

  return `${minutes}:${paddedSeconds}`;
}

type VideoElementWithPictureInPicture = HTMLVideoElement & {
  requestPictureInPicture?: () => Promise<unknown>;
};

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/gu, '&amp;')
    .replace(/"/gu, '&quot;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;');
}

function buildHtmlAttachmentCspMeta() {
  return `<meta http-equiv="Content-Security-Policy" content="${escapeHtmlAttribute(
    [
      "default-src 'none'",
      "script-src 'none'",
      "connect-src 'none'",
      "frame-src 'none'",
      "object-src 'none'",
      "base-uri 'none'",
      "form-action 'none'",
      "img-src 'self' data: blob:",
      'font-src data:',
      "style-src 'unsafe-inline'",
    ].join('; ')
  )}">`;
}

function withHtmlAttachmentCsp(srcdoc: string): string {
  const cspMeta = buildHtmlAttachmentCspMeta();
  if (/<head(?:\s[^>]*)?>/iu.test(srcdoc)) {
    return srcdoc.replace(
      /<head(?:\s[^>]*)?>/iu,
      match => `${match}${cspMeta}`
    );
  }
  if (/<html(?:\s[^>]*)?>/iu.test(srcdoc)) {
    return srcdoc.replace(
      /<html(?:\s[^>]*)?>/iu,
      match => `${match}<head>${cspMeta}</head>`
    );
  }
  return `<!doctype html><html><head>${cspMeta}</head><body>${srcdoc}</body></html>`;
}

@customElement('affine-html-attachment-embed')
export class HtmlAttachmentEmbed extends LitElement {
  static override styles = css`
    :host {
      position: relative;
      display: block;
      width: 100%;
      height: 100%;
    }

    iframe {
      display: block;
      width: 100%;
      height: 100%;
      border: 0;
      color-scheme: auto;
    }

    .affine-attachment-embed-event-mask {
      position: absolute;
      inset: 0;
    }

    :host([interactive]) .affine-attachment-embed-event-mask {
      display: none;
    }
  `;

  @property({ attribute: false })
  accessor blobUrl = '';

  @property({ attribute: false })
  accessor loadBlob: (() => Promise<Blob | null>) | undefined;

  @property({ type: Boolean, reflect: true })
  accessor interactive = false;

  @state()
  private accessor _srcdoc: string | null = null;

  @state()
  private accessor _error = false;

  private _loadVersion = 0;

  private readonly _dispatchFocusOut = () => {
    this.dispatchEvent(
      new CustomEvent(HTML_ATTACHMENT_FOCUS_OUT_EVENT, {
        bubbles: true,
        composed: true,
      })
    );
  };

  protected override updated(changedProperties: PropertyValues<this>) {
    if (changedProperties.has('blobUrl')) {
      void this._loadSrcdoc();
    }
  }

  private async _loadSrcdoc() {
    const blobUrl = this.blobUrl;
    const loadVersion = ++this._loadVersion;
    this._srcdoc = null;
    this._error = false;

    if (!blobUrl) return;

    try {
      const blob = await this.loadBlob?.();
      if (!blob) throw new Error('HTML attachment blob is unavailable');
      const srcdoc = await blob.text();
      if (loadVersion !== this._loadVersion) return;
      this._srcdoc = withHtmlAttachmentCsp(srcdoc);
    } catch (error) {
      if (loadVersion !== this._loadVersion) return;
      this._error = true;
      console.error(error);
    }
  }

  override render() {
    if (this._error) {
      return html`<div
        style=${styleMap({
          display: 'grid',
          placeItems: 'center',
          width: '100%',
          height: '100%',
          font: '14px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        })}
      >
        HTML preview unavailable
      </div>`;
    }

    if (this._srcdoc === null) {
      return nothing;
    }

    return html`
      <iframe
        @blur=${this._dispatchFocusOut}
        sandbox="allow-popups allow-modals"
        srcdoc=${this._srcdoc}
        loading="lazy"
        scrolling="yes"
        frameborder="no"
        credentialless
      ></iframe>
      <div class="affine-attachment-embed-event-mask"></div>
    `;
  }
}

function clampMediaTime(time: number, duration: number) {
  if (!Number.isFinite(time) || time < 0) return 0;
  if (!Number.isFinite(duration) || duration <= 0) return time;
  return Math.min(time, duration);
}

function mediaControlLabel(key: string) {
  return (edgelessToolbarI18nT as (key: string) => string)(key);
}

@customElement('affine-attachment-media-presence-pill')
export class AttachmentMediaPresencePill extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      right: calc(
        max(16px, env(safe-area-inset-right)) +
          var(--streamlain-ai-island-avoid-inline-end, 0px)
      );
      bottom: max(16px, env(safe-area-inset-bottom));
      display: block;
      width: min(
        392px,
        calc(100vw - 32px - var(--streamlain-ai-island-avoid-inline-end, 0px))
      );
      z-index: 1000;
      color: var(--affine-text-primary-color, #f7f7f7);
      font-family: var(--streamlain-font-family);
      pointer-events: auto;
      transition:
        right 0.22s ease,
        width 0.22s ease;
    }

    :host([canvas-scoped]) {
      position: absolute;
      right: calc(16px + var(--streamlain-ai-island-avoid-inline-end, 0px));
      bottom: 16px;
      width: min(
        392px,
        calc(100% - 32px - var(--streamlain-ai-island-avoid-inline-end, 0px))
      );
      z-index: calc(var(--streamlain-z-index-popover) + 1);
    }

    .affine-attachment-media-presence-pill {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 10px;
      padding: 9px 10px 9px 12px;
      border: 1px solid var(--affine-border-color, rgb(255 255 255 / 14%));
      border-radius: 10px;
      background: var(
        --affine-background-overlay-panel-color,
        rgb(18 18 18 / 94%)
      );
      box-shadow: 0 16px 44px rgb(0 0 0 / 34%);
      backdrop-filter: blur(12px);
    }

    .affine-attachment-media-presence-main {
      display: grid;
      grid-template-columns: 28px minmax(0, 1fr);
      align-items: center;
      gap: 10px;
      min-width: 0;
      padding: 0;
      border: 0;
      background: transparent;
      color: inherit;
      text-align: left;
      cursor: pointer;
    }

    .affine-attachment-media-presence-main:hover
      .affine-attachment-media-presence-title,
    .affine-attachment-media-presence-main:focus-visible
      .affine-attachment-media-presence-title {
      color: var(--affine-primary-color, #69a7ff);
    }

    .affine-attachment-media-presence-icon {
      display: grid;
      place-items: center;
      width: 28px;
      height: 28px;
      border-radius: 8px;
      background: var(--affine-hover-color, rgb(255 255 255 / 10%));
      color: var(--affine-primary-color, #69a7ff);
    }

    .affine-attachment-media-presence-icon svg {
      width: 18px;
      height: 18px;
    }

    .affine-attachment-media-presence-copy {
      min-width: 0;
    }

    .affine-attachment-media-presence-title {
      overflow: hidden;
      color: var(--affine-text-primary-color, #fff);
      font-size: 13px;
      font-weight: 600;
      line-height: 18px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .affine-attachment-media-presence-subtitle {
      overflow: hidden;
      color: var(--affine-text-secondary-color, rgb(255 255 255 / 62%));
      font-size: 12px;
      line-height: 17px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .affine-attachment-media-presence-actions {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }

    .affine-attachment-media-presence-action {
      display: inline-grid;
      place-items: center;
      min-width: 68px;
      height: 32px;
      padding: 0 10px;
      border: 0;
      border-radius: 8px;
      background: var(--affine-primary-color, #1e96ff);
      color: var(--affine-pure-white, #fff);
      font: inherit;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      white-space: nowrap;
      cursor: pointer;
    }

    .affine-attachment-media-presence-action.secondary {
      min-width: 36px;
      padding: 0 9px;
      background: var(--affine-hover-color, rgb(255 255 255 / 10%));
      color: var(--affine-text-primary-color, #fff);
    }

    .affine-attachment-media-presence-action:hover,
    .affine-attachment-media-presence-action:focus-visible {
      filter: brightness(1.08);
      outline: none;
    }

    @media (max-width: 520px) {
      :host {
        right: calc(
          max(10px, env(safe-area-inset-right)) +
            var(--streamlain-ai-island-avoid-inline-end, 0px)
        );
        bottom: max(10px, env(safe-area-inset-bottom));
        width: min(
          calc(
            100vw - 20px - var(--streamlain-ai-island-avoid-inline-end, 0px)
          ),
          360px
        );
      }

      :host([canvas-scoped]) {
        right: calc(10px + var(--streamlain-ai-island-avoid-inline-end, 0px));
        bottom: 10px;
        width: min(
          calc(100% - 20px - var(--streamlain-ai-island-avoid-inline-end, 0px)),
          360px
        );
      }

      .affine-attachment-media-presence-pill {
        grid-template-columns: minmax(0, 1fr);
      }

      .affine-attachment-media-presence-actions {
        justify-content: flex-end;
      }
    }
  `;

  @property({ attribute: false })
  accessor onFocusMedia: (() => void) | undefined;

  @property({ attribute: false })
  accessor onFollowMedia:
    | ((options: AttachmentMediaFollowOptions) => void)
    | undefined;

  @property({ attribute: false })
  accessor onLeaveMedia: (() => void) | undefined;

  @property({ attribute: false })
  accessor remote: AttachmentMediaPresenceRemote | null = null;

  @property({ type: Boolean })
  accessor following = false;

  @property({ attribute: 'canvas-scoped', reflect: true, type: Boolean })
  accessor canvasScoped = false;

  private readonly _focusMedia = (event: Event) => {
    stopVideoEmbedEvent(event);
    this.onFocusMedia?.();
  };

  private readonly _leaveMedia = (event: Event) => {
    stopVideoEmbedEvent(event);
    this.onLeaveMedia?.();
  };

  private readonly _followMuted = (event: Event) => {
    stopVideoEmbedEvent(event);
    this.onFollowMedia?.({ muted: true });
  };

  private readonly _followWithSound = (event: Event) => {
    stopVideoEmbedEvent(event);
    this.onFollowMedia?.({ muted: false });
  };

  override render() {
    const remote = this.remote;
    if (!remote) return nothing;

    const { presence, userName } = remote;
    const actionLabel = this.following
      ? mediaControlLabel('toolbar.leaveMedia')
      : presence.moderated
        ? mediaControlLabel('toolbar.followMedia')
        : mediaControlLabel('toolbar.joinMedia');
    const statusParts = this.following
      ? [
          mediaControlLabel('toolbar.followingMedia'),
          userName,
          formatVideoTime(presence.currentTime),
        ]
      : [userName, formatVideoTime(presence.currentTime)];
    if (!presence.playing) {
      statusParts.push(mediaControlLabel('toolbar.mediaPaused'));
    }
    if (presence.muted) {
      statusParts.push(mediaControlLabel('toolbar.mediaMuted'));
    }
    const subtitle = statusParts.join(' · ');

    return html`
      <div class="affine-attachment-media-presence-pill">
        <button
          class="affine-attachment-media-presence-main"
          aria-label=${mediaControlLabel('toolbar.focusMedia')}
          title=${mediaControlLabel('toolbar.focusMedia')}
          @click=${this._focusMedia}
        >
          <span
            class="affine-attachment-media-presence-icon"
            style=${styleMap({
              color: remote.color ?? 'var(--affine-primary-color, #69a7ff)',
            })}
          >
            ${PlayFillIcon()}
          </span>
          <span class="affine-attachment-media-presence-copy">
            <span class="affine-attachment-media-presence-title">
              ${presence.name}
            </span>
            <span class="affine-attachment-media-presence-subtitle">
              ${subtitle}
            </span>
          </span>
        </button>
        <span class="affine-attachment-media-presence-actions">
          <button
            class=${`affine-attachment-media-presence-action${
              this.following ? ' secondary' : ''
            }`}
            @click=${this.following ? this._leaveMedia : this._followWithSound}
          >
            ${actionLabel}
          </button>
          ${presence.moderated && !this.following
            ? html`
                <button
                  class="affine-attachment-media-presence-action secondary"
                  title=${mediaControlLabel('toolbar.mute')}
                  aria-label=${mediaControlLabel('toolbar.mute')}
                  @click=${this._followMuted}
                >
                  ${VolumePanelIcon()}
                </button>
              `
            : nothing}
        </span>
      </div>
    `;
  }
}

@customElement('affine-media-attachment-embed')
export class MediaAttachmentEmbed extends LitElement {
  static override styles = css`
    :host {
      position: relative;
      display: block;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #000;
      color: #fff;
      font-family: var(--streamlain-font-family);
      user-select: none;
    }

    .affine-media-attachment-shell {
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #000;
    }

    .affine-media-attachment-element {
      display: block;
      width: 100%;
      height: 100%;
      background: #000;
      object-fit: contain;
    }

    .affine-media-attachment-audio-element {
      position: absolute;
      inset: 0;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    }

    .affine-media-attachment-audio-surface {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 20px 22px 64px;
      box-sizing: border-box;
      background:
        linear-gradient(135deg, rgb(17 23 31 / 96%) 0%, rgb(5 8 12) 100%), #000;
    }

    .affine-media-attachment-audio-mark {
      display: grid;
      place-items: center;
      flex: 0 0 auto;
      width: 44px;
      height: 44px;
      border: 1px solid rgb(255 255 255 / 16%);
      border-radius: 8px;
      background: rgb(255 255 255 / 8%);
      color: rgb(255 255 255 / 90%);
    }

    .affine-media-attachment-audio-copy {
      min-width: 0;
    }

    .affine-media-attachment-audio-title {
      overflow: hidden;
      color: rgb(255 255 255 / 92%);
      font-size: 15px;
      font-weight: 600;
      line-height: 22px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .affine-media-attachment-audio-meta {
      color: rgb(255 255 255 / 58%);
      font-size: 12px;
      line-height: 18px;
    }

    .affine-media-attachment-controls {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      display: grid;
      grid-template-columns:
        auto minmax(64px, auto) minmax(48px, 1fr) auto minmax(44px, 80px)
        auto auto;
      align-items: center;
      gap: 7px;
      min-height: 50px;
      padding: 11px 12px 8px;
      box-sizing: border-box;
      background: linear-gradient(
        180deg,
        rgb(0 0 0 / 0%) 0%,
        rgb(0 0 0 / 78%) 100%
      );
      opacity: 0;
      transform: translateY(6px);
      transition:
        opacity 0.16s ease,
        transform 0.16s ease;
    }

    :host([media-kind='audio']) .affine-media-attachment-controls,
    :host(:hover) .affine-media-attachment-controls,
    :host(:focus-within) .affine-media-attachment-controls,
    :host([menu-open]) .affine-media-attachment-controls {
      opacity: 1;
      transform: translateY(0);
    }

    .affine-media-attachment-button {
      display: inline-grid;
      place-items: center;
      flex: 0 0 auto;
      width: 32px;
      height: 32px;
      padding: 0;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: #fff;
      cursor: pointer;
    }

    .affine-media-attachment-button:hover,
    .affine-media-attachment-button:focus-visible {
      background: rgb(255 255 255 / 14%);
      outline: none;
    }

    .affine-media-attachment-button svg {
      width: 20px;
      height: 20px;
    }

    .affine-media-attachment-time {
      flex: 0 0 auto;
      min-width: 74px;
      font-size: 12px;
      line-height: 20px;
      color: rgb(255 255 255 / 86%);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }

    .affine-media-attachment-seek,
    .affine-media-attachment-volume {
      min-width: 0;
      accent-color: #fff;
      cursor: pointer;
    }

    .affine-media-attachment-seek {
      width: 100%;
    }

    .affine-media-attachment-volume {
      width: 100%;
      max-width: 80px;
    }

    .affine-media-attachment-menu {
      position: absolute;
      right: 12px;
      bottom: 52px;
      width: min(236px, calc(100% - 24px));
      max-height: min(292px, calc(100% - 60px));
      overflow: auto;
      padding: 5px;
      border: 1px solid rgb(255 255 255 / 14%);
      border-radius: 8px;
      box-sizing: border-box;
      background: var(
        --affine-background-overlay-panel-color,
        rgb(18 18 18 / 96%)
      );
      box-shadow: 0 14px 36px rgb(0 0 0 / 42%);
      color: var(--affine-text-primary-color, rgb(255 255 255 / 92%));
      z-index: 1;
      backdrop-filter: blur(10px);
      scrollbar-width: thin;
      scrollbar-color: rgb(255 255 255 / 24%) transparent;
    }

    .affine-media-attachment-menu-label {
      padding: 6px 8px 3px;
      font-size: 11px;
      line-height: 16px;
      color: rgb(255 255 255 / 58%);
    }

    .affine-media-attachment-menu-item {
      display: flex;
      align-items: center;
      gap: 9px;
      width: 100%;
      min-height: 28px;
      padding: 4px 8px;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: inherit;
      font: inherit;
      font-size: 13px;
      line-height: 20px;
      text-align: left;
      cursor: pointer;
    }

    .affine-media-attachment-menu-item:hover,
    .affine-media-attachment-menu-item:focus-visible {
      background: rgb(255 255 255 / 10%);
      outline: none;
    }

    .affine-media-attachment-menu-item[data-selected] {
      color: #69a7ff;
      font-weight: 600;
    }

    .affine-media-attachment-menu-item svg {
      flex: 0 0 auto;
      width: 18px;
      height: 18px;
    }

    .affine-media-attachment-menu-pip-label {
      display: inline-grid;
      place-items: center;
      width: 28px;
      height: 18px;
      border: 1px solid currentColor;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
    }

    .affine-media-attachment-event-mask {
      position: absolute;
      inset: 0;
    }

    @media (max-width: 520px) {
      .affine-media-attachment-controls {
        grid-template-columns: auto minmax(48px, 1fr) auto auto;
      }

      .affine-media-attachment-time,
      .affine-media-attachment-volume {
        display: none;
      }
    }
  `;

  @property({ attribute: false })
  accessor blobUrl = '';

  @property({ attribute: false })
  accessor fileName = 'video';

  @property({ reflect: true, attribute: 'media-kind' })
  accessor mediaKind: 'audio' | 'video' = 'video';

  @property({ type: Boolean })
  accessor interactive = false;

  @property({ attribute: false })
  accessor onPresenceChange:
    | ((update: AttachmentMediaPresenceUpdate) => void)
    | undefined;

  @property({ type: Boolean, reflect: true, attribute: 'menu-open' })
  accessor menuOpen = false;

  @query('.affine-media-attachment-element')
  private accessor _media!: HTMLMediaElement;

  @state()
  private accessor _currentTime = 0;

  @state()
  private accessor _duration = 0;

  @state()
  private accessor _muted = false;

  @state()
  private accessor _playing = false;

  @state()
  private accessor _playbackRate = 1;

  @state()
  private accessor _volume = 1;

  private _lastTimePresenceAt = 0;

  private _pendingRemoteApply: {
    options: AttachmentMediaFollowOptions;
    presence: AttachmentMediaPresenceState;
  } | null = null;

  private get _seekValue() {
    if (this._duration <= 0) return 0;
    return Math.round((this._currentTime / this._duration) * 1000);
  }

  private get _timeLabel() {
    return `${formatVideoTime(this._currentTime)} / ${formatVideoTime(
      this._duration
    )}`;
  }

  applyRemotePresence(
    presence: AttachmentMediaPresenceState,
    options: AttachmentMediaFollowOptions
  ) {
    const media = this._media;
    if (!media || media.readyState === 0) {
      this._pendingRemoteApply = { presence, options };
      media?.load();
      return;
    }

    const elapsedSeconds = presence.playing
      ? ((Date.now() - presence.updatedAt) / 1000) * presence.playbackRate
      : 0;
    const nextTime = clampMediaTime(
      presence.currentTime + elapsedSeconds,
      media.duration
    );

    media.playbackRate = presence.playbackRate || 1;
    media.muted = options.muted;
    if (Math.abs(media.currentTime - nextTime) > 0.75) {
      media.currentTime = nextTime;
    }

    if (presence.playing) {
      media.play().catch(console.error);
    } else {
      media.pause();
    }

    this._syncState();
  }

  private readonly _download = (event: Event) => {
    stopVideoEmbedEvent(event);
    this.menuOpen = false;

    if (!this.blobUrl) return;

    const link = document.createElement('a');
    link.download = this.fileName || 'video';
    link.href = this.blobUrl;
    link.rel = 'noopener';
    link.click();
    link.remove();
  };

  private readonly _requestFullscreen = (event: Event) => {
    stopVideoEmbedEvent(event);
    this.menuOpen = false;

    if (document.fullscreenElement === this) {
      document.exitFullscreen().catch(console.error);
      return;
    }

    const request = this.requestFullscreen?.();
    request?.catch(console.error);
  };

  private readonly _requestPictureInPicture = (event: Event) => {
    stopVideoEmbedEvent(event);
    this.menuOpen = false;

    const video = this._media as VideoElementWithPictureInPicture | undefined;
    video?.requestPictureInPicture?.().catch(console.error);
  };

  private readonly _seek = (event: Event) => {
    stopVideoEmbedEvent(event);

    if (!this._media || this._duration <= 0) return;

    const value = Number((event.target as HTMLInputElement).value);
    this._media.currentTime = (value / 1000) * this._duration;
  };

  private readonly _publishSeek = (event: Event) => {
    stopVideoEmbedEvent(event);
    this._publishPresence('seek', true);
  };

  private readonly _setPlaybackRate = (event: Event, rate: number) => {
    stopVideoEmbedEvent(event);
    this.menuOpen = false;

    if (!this._media) return;

    this._media.playbackRate = rate;
    this._playbackRate = rate;
    this._publishPresence('rate', true);
  };

  private readonly _setVolume = (event: Event) => {
    stopVideoEmbedEvent(event);

    if (!this._media) return;

    const value = Number((event.target as HTMLInputElement).value);
    this._media.volume = value;
    this._media.muted = value === 0;
    this._syncState();
    this._publishPresence('volume', true);
  };

  private readonly _stopInteractiveEvent = (event: Event) => {
    if (this.interactive) {
      stopVideoEmbedEvent(event);
    }
  };

  private _applyPendingRemotePresence() {
    if (!this._pendingRemoteApply) return;

    const pending = this._pendingRemoteApply;
    this._pendingRemoteApply = null;
    this.applyRemotePresence(pending.presence, pending.options);
  }

  private _publishPresence(
    action: AttachmentMediaPresenceUpdate['action'],
    force = false
  ) {
    if (!this.onPresenceChange || !this._media) return;

    const now = Date.now();
    if (action === 'time' && !force && now - this._lastTimePresenceAt < 2000) {
      return;
    }
    if (action === 'time') {
      this._lastTimePresenceAt = now;
    }

    this.onPresenceChange({
      action,
      currentTime: this._media.currentTime,
      duration: Number.isFinite(this._media.duration)
        ? this._media.duration
        : undefined,
      mediaKind: this.mediaKind,
      muted: this._media.muted,
      playbackRate: this._media.playbackRate,
      playing: !this._media.paused,
    });
  }

  private readonly _handleLoadedMetadata = () => {
    this._syncState();
    this._applyPendingRemotePresence();
  };

  private readonly _handlePause = () => {
    this._syncState();
    this._publishPresence('pause', true);
  };

  private readonly _handlePlay = () => {
    this._syncState();
    this._publishPresence('play', true);
  };

  private readonly _handleTimeUpdate = () => {
    this._syncState();
    if (this._playing) {
      this._publishPresence('time');
    }
  };

  private readonly _handleKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !this.menuOpen) return;
    stopVideoEmbedEvent(event);
    this.menuOpen = false;
  };

  private readonly _syncState = () => {
    const media = this._media;
    if (!media) return;

    this._currentTime = media.currentTime;
    this._duration = Number.isFinite(media.duration) ? media.duration : 0;
    this._muted = media.muted;
    this._playing = !media.paused;
    this._playbackRate = media.playbackRate;
    this._volume = media.volume;
  };

  private readonly _toggleMenu = (event: Event) => {
    stopVideoEmbedEvent(event);
    this.menuOpen = !this.menuOpen;
  };

  private readonly _toggleMute = (event: Event) => {
    stopVideoEmbedEvent(event);

    if (!this._media) return;

    this._media.muted = !this._media.muted;
    this._syncState();
    this._publishPresence('volume', true);
  };

  private readonly _togglePlay = (event: Event) => {
    stopVideoEmbedEvent(event);

    if (!this._media) return;

    if (this._media.paused) {
      this._media.play().catch(console.error);
    } else {
      this._media.pause();
    }
  };

  private _renderMediaElement() {
    const events = {
      '@durationchange': this._syncState,
      '@loadedmetadata': this._handleLoadedMetadata,
      '@pause': this._handlePause,
      '@play': this._handlePlay,
      '@ratechange': this._syncState,
      '@seeked': this._publishSeek,
      '@timeupdate': this._handleTimeUpdate,
      '@volumechange': this._syncState,
    };

    if (this.mediaKind === 'audio') {
      return html`
        <audio
          class="affine-media-attachment-element affine-media-attachment-audio-element"
          src=${this.blobUrl}
          preload="metadata"
          @durationchange=${events['@durationchange']}
          @loadedmetadata=${events['@loadedmetadata']}
          @pause=${events['@pause']}
          @play=${events['@play']}
          @ratechange=${events['@ratechange']}
          @seeked=${events['@seeked']}
          @timeupdate=${events['@timeupdate']}
          @volumechange=${events['@volumechange']}
        ></audio>
        <div class="affine-media-attachment-audio-surface">
          <div class="affine-media-attachment-audio-mark">
            ${VolumePanelIcon()}
          </div>
          <div class="affine-media-attachment-audio-copy">
            <div class="affine-media-attachment-audio-title">
              ${this.fileName}
            </div>
            <div class="affine-media-attachment-audio-meta">
              ${this._timeLabel}
            </div>
          </div>
        </div>
      `;
    }

    return html`<video
      class="affine-media-attachment-element"
      src=${this.blobUrl}
      preload="metadata"
      @click=${this._togglePlay}
      @dblclick=${this._requestFullscreen}
      @durationchange=${events['@durationchange']}
      @loadedmetadata=${events['@loadedmetadata']}
      @pause=${events['@pause']}
      @play=${events['@play']}
      @ratechange=${events['@ratechange']}
      @seeked=${events['@seeked']}
      @timeupdate=${events['@timeupdate']}
      @volumechange=${events['@volumechange']}
    ></video>`;
  }

  override render() {
    return html`
      <div
        class="affine-media-attachment-shell"
        @keydown=${this._handleKeydown}
        @click=${this._stopInteractiveEvent}
        @contextmenu=${this._stopInteractiveEvent}
        @dblclick=${this._stopInteractiveEvent}
        @mousedown=${this._stopInteractiveEvent}
        @mouseup=${this._stopInteractiveEvent}
        @pointerdown=${this._stopInteractiveEvent}
        @pointerup=${this._stopInteractiveEvent}
        @wheel=${this._stopInteractiveEvent}
      >
        ${this._renderMediaElement()}
        <div class="affine-media-attachment-controls">
          <button
            class="affine-media-attachment-button"
            title=${this._playing
              ? mediaControlLabel('toolbar.pause')
              : mediaControlLabel('toolbar.play')}
            aria-label=${this._playing
              ? mediaControlLabel('toolbar.pause')
              : mediaControlLabel('toolbar.play')}
            @click=${this._togglePlay}
          >
            ${this._playing ? PausePanelIcon() : PlayFillIcon()}
          </button>
          <span class="affine-media-attachment-time">${this._timeLabel}</span>
          <input
            class="affine-media-attachment-seek"
            type="range"
            min="0"
            max="1000"
            step="1"
            .value=${String(this._seekValue)}
            ?disabled=${this._duration <= 0}
            aria-label=${mediaControlLabel('toolbar.seek')}
            @input=${this._seek}
            @change=${this._publishSeek}
          />
          <button
            class="affine-media-attachment-button"
            title=${this._muted
              ? mediaControlLabel('toolbar.unmute')
              : mediaControlLabel('toolbar.mute')}
            aria-label=${this._muted
              ? mediaControlLabel('toolbar.unmute')
              : mediaControlLabel('toolbar.mute')}
            @click=${this._toggleMute}
          >
            ${VolumePanelIcon()}
          </button>
          <input
            class="affine-media-attachment-volume"
            type="range"
            min="0"
            max="1"
            step="0.05"
            .value=${String(this._muted ? 0 : this._volume)}
            aria-label=${mediaControlLabel('toolbar.volume')}
            @input=${this._setVolume}
          />
          ${this.mediaKind === 'video'
            ? html`
                <button
                  class="affine-media-attachment-button"
                  title=${mediaControlLabel('toolbar.fullscreen')}
                  aria-label=${mediaControlLabel('toolbar.fullscreen')}
                  @click=${this._requestFullscreen}
                >
                  ${ExpandFullIcon()}
                </button>
              `
            : nothing}
          <button
            class="affine-media-attachment-button"
            title=${mediaControlLabel('toolbar.more')}
            aria-label=${mediaControlLabel('toolbar.more')}
            aria-expanded=${this.menuOpen ? 'true' : 'false'}
            @click=${this._toggleMenu}
          >
            ${MoreVerticalIcon()}
          </button>
        </div>
        ${this.menuOpen
          ? html`
              <div
                class="affine-media-attachment-menu"
                @click=${stopVideoEmbedEvent}
                @contextmenu=${stopVideoEmbedEvent}
                @mousedown=${stopVideoEmbedEvent}
                @mouseup=${stopVideoEmbedEvent}
                @pointerdown=${stopVideoEmbedEvent}
                @pointerup=${stopVideoEmbedEvent}
              >
                <button
                  class="affine-media-attachment-menu-item"
                  @click=${this._download}
                >
                  ${DownloadIcon()}
                  <span>${mediaControlLabel('toolbar.download')}</span>
                </button>
                ${this.mediaKind === 'video'
                  ? html`
                      <button
                        class="affine-media-attachment-menu-item"
                        @click=${this._requestPictureInPicture}
                      >
                        <span class="affine-media-attachment-menu-pip-label">
                          PiP
                        </span>
                        <span>
                          ${mediaControlLabel('toolbar.pictureInPicture')}
                        </span>
                      </button>
                    `
                  : nothing}
                <div class="affine-media-attachment-menu-label">
                  ${mediaControlLabel('toolbar.playbackSpeed')}
                </div>
                ${VIDEO_PLAYBACK_RATES.map(
                  rate => html`
                    <button
                      class="affine-media-attachment-menu-item"
                      ?data-selected=${this._playbackRate === rate}
                      @click=${(event: Event) =>
                        this._setPlaybackRate(event, rate)}
                    >
                      <span>${rate}x</span>
                    </button>
                  `
                )}
              </div>
            `
          : nothing}
        ${this.interactive
          ? nothing
          : html`<div class="affine-media-attachment-event-mask"></div>`}
      </div>
    `;
  }
}

const embedConfig: AttachmentEmbedConfig[] = [
  {
    name: 'image',
    shouldBeConverted: true,
    check: model =>
      model.store.schema.flavourSchemaMap.has('affine:image') &&
      model.props.type.startsWith('image/'),
    async action(model, std) {
      const component = std.view.getBlock(model.id);
      if (!component) return;

      await turnIntoImageBlock(model);
    },
  },
  {
    name: 'pdf',
    shouldShowStatus: true,
    check: (model, maxFileSize) =>
      model.props.type === 'application/pdf' && model.props.size <= maxFileSize,
    action: model => {
      const bound = Bound.deserialize(model.props.xywh);
      bound.w = EMBED_CARD_WIDTH.pdf;
      bound.h = EMBED_CARD_HEIGHT.pdf;
      model.store.updateBlock(model, {
        embed: true,
        style: 'pdf',
        xywh: bound.serialize(),
      });
    },
    render: (_, blobUrl) => {
      // More options: https://tinytip.co/tips/html-pdf-params/
      // https://chromium.googlesource.com/chromium/src/+/refs/tags/121.0.6153.1/chrome/browser/resources/pdf/open_pdf_params_parser.ts
      const parameters = '#toolbar=0';
      return html`
        <iframe
          style=${styleMap({
            width: '100%',
            minHeight: '480px',
            colorScheme: 'auto',
          })}
          src=${blobUrl + parameters}
          loading="lazy"
          scrolling="no"
          frameborder="no"
          allowTransparency
          allowfullscreen
          type="application/pdf"
          credentialless
        ></iframe>
        <div class="affine-attachment-embed-event-mask"></div>
      `;
    },
  },
  {
    name: 'html',
    shouldShowStatus: true,
    rerenderOnContextChange: true,
    check: (model, maxFileSize) =>
      isHtmlAttachment(model) && model.props.size <= maxFileSize,
    action: model => {
      const bound = Bound.deserialize(model.props.xywh);
      bound.w = EMBED_CARD_WIDTH.html;
      bound.h = getHtmlEmbedHeight();
      model.store.updateBlock(model, {
        embed: true,
        style: 'html',
        xywh: bound.serialize(),
      });
    },
    render: (_, blobUrl, context) => {
      return html`<affine-html-attachment-embed
        style=${styleMap({
          display: 'block',
          width: '100%',
          height: '100%',
        })}
        .blobUrl=${blobUrl}
        .loadBlob=${context.loadBlob}
        .interactive=${context.interactive}
      ></affine-html-attachment-embed>`;
    },
  },
  {
    name: 'text',
    shouldShowStatus: true,
    check: model =>
      isTextAttachment(model) &&
      model.props.size <= TEXT_ATTACHMENT_PREVIEW_MAX_BYTES,
    action: model => {
      const bound = Bound.deserialize(model.props.xywh);
      bound.w = EMBED_CARD_WIDTH.html;
      bound.h = getHtmlEmbedHeight();
      model.store.updateBlock(model, {
        embed: true,
        style: 'html',
        xywh: bound.serialize(),
      });
    },
    render: (_, __, context) => html`
      <affine-text-attachment-embed
        .loadBlob=${context.loadBlob}
      ></affine-text-attachment-embed>
    `,
  },
  {
    name: 'video',
    shouldShowStatus: true,
    rerenderOnContextChange: true,
    check: (model, maxFileSize) =>
      model.props.type.startsWith('video/') && model.props.size <= maxFileSize,
    async action(model) {
      const bound = Bound.deserialize(model.props.xywh);
      const blob = await getAttachmentBlob(model);
      const videoSize = blob
        ? await readVideoSize(blob)
        : { width: 0, height: 0 };

      bound.w = EMBED_CARD_WIDTH.video;
      bound.h = getVideoEmbedHeightForWidth(bound.w, videoSize);
      model.store.updateBlock(model, {
        embed: true,
        style: 'video',
        xywh: bound.serialize(),
      });
    },
    render: (model, blobUrl, context) =>
      html`<affine-media-attachment-embed
        style=${styleMap({
          display: 'block',
          width: '100%',
          height: '100%',
        })}
        .blobUrl=${blobUrl}
        .fileName=${resolveAttachmentBlockDisplayName(model)}
        .interactive=${context.interactive}
        .mediaKind=${'video'}
        .onPresenceChange=${context.publishMediaPresence}
      ></affine-media-attachment-embed>`,
  },
  {
    name: 'audio',
    check: (model, maxFileSize) =>
      model.props.type.startsWith('audio/') && model.props.size <= maxFileSize,
    render: (model, blobUrl, context) =>
      html`<affine-media-attachment-embed
        style=${styleMap({
          display: 'block',
          width: '100%',
          height: '100%',
        })}
        .blobUrl=${blobUrl}
        .fileName=${resolveAttachmentBlockDisplayName(model)}
        .interactive=${context.interactive}
        .mediaKind=${'audio'}
        .onPresenceChange=${context.publishMediaPresence}
      ></affine-media-attachment-embed>`,
  },
];

/**
 * Turn the attachment block into an image block.
 */
async function turnIntoImageBlock(model: AttachmentBlockModel) {
  if (!model.store.schema.flavourSchemaMap.has('affine:image')) {
    console.error('The image flavour is not supported!');
    return;
  }

  const sourceId = model.props.sourceId;
  if (!sourceId) return;

  const { getImageData } = withTempBlobData();

  let imageSize = model.props.sourceId
    ? getImageData(model.props.sourceId)
    : undefined;

  const bounds = model.xywh
    ? Bound.fromXYWH(model.deserializedXYWH)
    : undefined;

  if (bounds) {
    if (!imageSize?.width || !imageSize?.height) {
      const blob = await getAttachmentBlob(model);
      if (blob) {
        imageSize = await readImageSize(blob);
      }
    }

    if (imageSize?.width && imageSize?.height) {
      const p = imageSize.height / imageSize.width;
      imageSize.width = Math.min(imageSize.width, MAX_IMAGE_WIDTH);
      imageSize.height = imageSize.width * p;
      bounds.w = imageSize.width;
      bounds.h = imageSize.height;
    }
  }

  const others = bounds ? { xywh: bounds.serialize() } : undefined;

  const imageProp: Partial<ImageBlockProps> = {
    sourceId,
    caption: model.props.caption,
    name: resolveAttachmentBlockDisplayName(model),
    size: model.props.size,
    type: model.props.type,
    ...imageSize,
    ...others,
  };
  transformModel(model, 'affine:image', imageProp);
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-attachment-media-presence-pill': AttachmentMediaPresencePill;
    'affine-media-attachment-embed': MediaAttachmentEmbed;
  }
}
